import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ITooltipParams } from 'ag-grid-community';
import type { CustomTooltipProps } from 'ag-grid-react';
import { Label, TextInput } from '@hm-group/bads-react';
import './MultiSeasonTooltip.css';
import { PLANLEVEL_GROUND } from '../../constants';
import { isNonPrimaryBeforeSplit } from '../../helpers/gridUtilities';

/**
 * Event for multi-season tooltip double-click, mimicking Angular's DataTransferService.
 * When a cell is double-clicked:
 * 1. Dispatch { show: false, field: null } to close all existing tooltips
 * 2. Dispatch { show: true, field: 'Q1_P1_W1' } to open tooltip for specific field
 * Each tooltip checks if the field matches before displaying.
 */
export const MST_DOUBLE_CLICK_EVENT = 'mst:doubleclick';

interface MultiSeasonTooltipParams extends ITooltipParams {
    isGridEditable: boolean;
    cellFieldNameToWeekMap: Map<string, string>;
    weekToSeasonMap: Map<string, string[]>;
    currentWeek: string;
    isLatestSeason?: boolean;
    isOldSeason?: boolean;
    onTooltipChange?: (data: {
        field: string;
        isLatestSeason: boolean;
        kpi: string;
        value: number;
        isMainMarket: boolean;
    }) => void;
}

export const MultiSeasonTooltip = forwardRef((props: CustomTooltipProps, ref) => {
    const params = props as unknown as MultiSeasonTooltipParams;

    // Simple state management like Angular (no complex module flags)
    const field = params.colDef && 'field' in params.colDef ? params.colDef.field : undefined;
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const [endingSeasonValue, setEndingSeasonValue] = useState<number>(0);
    const [endingSeasonInput, setEndingSeasonInput] = useState<string>('0');
    const [latestSeasonInput, setLatestSeasonInput] = useState<string>('0');
    const [openOnLeft, setOpenOnLeft] = useState(false);
    const focusedCountRef = useRef(0);
    const endingSeasonInputRef = useRef(endingSeasonInput);
    const latestSeasonInputRef = useRef(latestSeasonInput);
    const endingSeasonValueRef = useRef(endingSeasonValue);
    const latestSeasonValueRef = useRef(0);
    const commitAllRef = useRef<() => void>(() => { /* replaced with the latest handlers after render */ });

    const containerRef = useRef<HTMLDivElement>(null);
    const location = params.location;
    const headerTooltip = params.colDef?.headerTooltip;
    const headerTooltipText =
        typeof headerTooltip === 'string' || typeof headerTooltip === 'number'
            ? String(headerTooltip)
            : '';
    const isOldSeasonEnabled = params.isOldSeason ?? true;
    const isLatestSeasonEnabled = params.isLatestSeason ?? true;

    // Extract data
    const kpiName = params.data?.['kpi'];
    const week = params.cellFieldNameToWeekMap?.get(field ?? '') ?? '';
    const isPastWeek = week !== '' && params.currentWeek !== '' && Number(week) < Number(params.currentWeek);
    const seasons = params.weekToSeasonMap?.get(week) ?? [];
    const endingSeasonLabel = seasons[0] ?? '';
    const latestSeasonLabel = seasons[1] ?? '';

    // Check if cell is readonly
    const isCalculated = params.data?.['isCalculated'];
    const isGroundRow = params.data?.['planLevel'] === PLANLEVEL_GROUND;
    const isConnectedMarket = !!params.data?.['isConnectedToMainMarket'];
    const isReadonlyGrid = !params.isGridEditable || isGroundRow || isConnectedMarket;

    // Non-primary successors: check each season individually for editability
    const rowData = (params.data ?? {}) as Record<string, unknown>;
    const isEndingSeasonNonPrimary = endingSeasonLabel
        ? isNonPrimaryBeforeSplit(rowData, week, new Map([[week, [endingSeasonLabel]]]))
        : false;
    const isLatestSeasonNonPrimary = latestSeasonLabel
        ? isNonPrimaryBeforeSplit(rowData, week, new Map([[week, [latestSeasonLabel]]]))
        : false;

    useImperativeHandle(ref, () => ({
        // Required by ag-grid
    }));

    // Initialize values from cell data
    useEffect(() => {
        if (location === 'cell' && !isCalculated && field) {
            const cellValue = Number(params.valueFormatted ?? params.value) || 0;
            const esValue = Number(params.data?.[field + '_es']) || 0;
            const latestValue = cellValue - esValue;

            setEndingSeasonValue(esValue);
            endingSeasonValueRef.current = esValue;
            latestSeasonValueRef.current = latestValue;
            setEndingSeasonInput(String(esValue));
            endingSeasonInputRef.current = String(esValue);
            setLatestSeasonInput(String(latestValue));
            latestSeasonInputRef.current = String(latestValue);
        }
    }, [location, isCalculated, field, params.value, params.valueFormatted, params.data]);

    // Subscribe to double-click event (matches Angular subscription)
    // Angular: this.dataTransferService.isCellDoubledClicked.subscribe((flag: boolean) => { this.displayTooltip = flag; })
    // Only show tooltip if the event is for this specific field
    useEffect(() => {
        const handleDoubleClick = (event: Event) => {
            const detail = (event as CustomEvent).detail as { show: boolean; field: string | null };
            
            // Show tooltip only if show is true AND field matches this tooltip's field
            if (detail.show && detail.field === field) {
                setDisplayTooltip(true);
            } else {
                // Close this tooltip if show is false or field doesn't match
                setDisplayTooltip(false);
            }
        };
        
        document.addEventListener(MST_DOUBLE_CLICK_EVENT, handleDoubleClick);
        return () => document.removeEventListener(MST_DOUBLE_CLICK_EVENT, handleDoubleClick);
    }, [field]);

    // ── Adjust tooltip position for viewport edge overflow ─────────
    // ag-grid positions the .ag-tooltip near the hovered cell by default.
    // We only nudge it with CSS transforms when it overflows the viewport,
    // matching the Angular approach.
    useEffect(() => {
        if (!displayTooltip) return;

        const adjustPosition = () => {
            const tooltipEl = containerRef.current?.closest('.ag-tooltip');
            if (!(tooltipEl instanceof HTMLElement)) return;

            // Reset any previous adjustments so we measure the natural position
            tooltipEl.style.transition = 'none';
            tooltipEl.style.transform = 'none';

            const rect = tooltipEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let translateX = 0;
            let translateY = 0;

            // Horizontal: shift left if overflowing right edge
            if (rect.right > viewportWidth - 20) {
                translateX = -(rect.right - viewportWidth + rect.width + 50);
                setOpenOnLeft(true);
            }

            // Vertical: shift up if overflowing bottom edge
            if (rect.bottom > viewportHeight - 10) {
                // Move the tooltip above the cell instead of below
                translateY = -(rect.height + 30);
            }

            if (translateX !== 0 || translateY !== 0) {
                tooltipEl.style.transform = `translate(${translateX}px, ${translateY}px)`;
            }
        };

        // setTimeout → RAF chain ensures the tooltip DOM is fully laid out
        const timeoutId = setTimeout(() => {
            requestAnimationFrame(() => {
                adjustPosition();
                // Second pass after content renders fully
                requestAnimationFrame(adjustPosition);
            });
        }, 5);

        return () => clearTimeout(timeoutId);
    }, [displayTooltip]);

    // Handle Enter key to commit and close
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && displayTooltip) {
                commitAllRef.current();
                setDisplayTooltip(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [displayTooltip]);

    // Handle click outside to commit and close tooltip
    useEffect(() => {
        if (!displayTooltip) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                commitAllRef.current();
                setDisplayTooltip(false);
            }
        };

        // Small delay to prevent immediate close on the same click that opened it
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [displayTooltip]);

    // ── Commit helpers (called on blur, Enter, and click-outside) ────

    const commitEndingSeason = () => {
        if (!field || isPastWeek) return;

        const currentInput = endingSeasonInputRef.current;
        const numValue = currentInput === '' ? 0 : Number(currentInput);
        const finalValue = Number.isNaN(numValue) ? 0 : numValue;

        // Normalize display
        setEndingSeasonInput(String(finalValue));
        endingSeasonInputRef.current = String(finalValue);

        // Skip API call if value hasn't changed (matches Angular's (change) behavior)
        if (finalValue === endingSeasonValueRef.current) return;

        setEndingSeasonValue(finalValue);
        endingSeasonValueRef.current = finalValue;

        params.onTooltipChange?.({
            field,
            isLatestSeason: false,
            kpi: params.data?.id,
            value: finalValue,
            isMainMarket: params.data?.['isMainMarket'],
        });

        if (params.data && field) {
            params.data[field + '_es'] = finalValue;
        }
    };

    const commitLatestSeason = () => {
        if (!field || isPastWeek) return;

        const currentInput = latestSeasonInputRef.current;
        const numValue = currentInput === '' ? 0 : Number(currentInput);
        const finalValue = Number.isNaN(numValue) ? 0 : numValue;

        // Normalize display
        setLatestSeasonInput(String(finalValue));
        latestSeasonInputRef.current = String(finalValue);

        // Skip API call if value hasn't changed (matches Angular's (change) behavior)
        if (finalValue === latestSeasonValueRef.current) return;

        latestSeasonValueRef.current = finalValue;

        params.onTooltipChange?.({
            field,
            isLatestSeason: true,
            kpi: params.data?.id,
            value: finalValue,
            isMainMarket: params.data?.['isMainMarket'],
        });

        if (params.data && field) {
            params.data[field] = endingSeasonValueRef.current + finalValue;
        }
    };

    // Document listeners are long-lived, so they call the latest commit handlers through this ref
    useEffect(() => {
        commitAllRef.current = () => {
            commitEndingSeason();
            commitLatestSeason();
        };
    });

    // ── BADS TextInput change handlers ────────────────────────────────

    const handleEndingSeasonChange = ({ value }: { value: string | number }) => {
        if (!isOldSeasonEnabled || !field || isPastWeek) return;

        const strValue = String(value);

        // Allow empty (user is deleting)
        if (strValue === '') {
            setEndingSeasonInput('');
            endingSeasonInputRef.current = '';
            return;
        }

        // Only allow non-negative integers up to 999
        if (!/^\d{0,3}$/.test(strValue)) return;

        setEndingSeasonInput(strValue);
        endingSeasonInputRef.current = strValue;
    };

    const handleEndingSeasonBlur = () => {
        handleInputBlur();
        commitEndingSeason();
    };

    const handleLatestSeasonChange = ({ value }: { value: string | number }) => {
        if (!isLatestSeasonEnabled || !field || isPastWeek) return;

        const strValue = String(value);

        // Allow empty (user is deleting)
        if (strValue === '') {
            setLatestSeasonInput('');
            latestSeasonInputRef.current = '';
            return;
        }

        // Only allow non-negative integers up to 999
        if (!/^\d{0,3}$/.test(strValue)) return;

        setLatestSeasonInput(strValue);
        latestSeasonInputRef.current = strValue;
    };

    const handleLatestSeasonBlur = () => {
        handleInputBlur();
        commitLatestSeason();
    };

    // ── Focus tracking for auto-close ─────────────────────────────────

    const handleInputFocus = () => {
        focusedCountRef.current += 1;
    };

    const handleInputBlur = () => {
        focusedCountRef.current -= 1;
    };

    // ── Header tooltip ────────────────────────────────────────────────

    if (location?.toString().includes('header')) {
        return (
            <div className="tooltip-container-mst flex-column">
                {headerTooltipText}
            </div>
        );
    }

    // ── Cell tooltip: transparent click target ─────────────────────────
    // Shown when ag-grid renders the tooltip (on hover) but user hasn't
    // double-clicked yet. Requires double-click to open editor (matching
    // the grid's onCellDoubleClicked pattern).
    if (location === 'cell' && !isCalculated && !displayTooltip) {
        return (
            <button
                // ag-grid 36 hides interactive tooltips on focusout; unmounting a focused button would trigger it
                onMouseDown={(e) => e.preventDefault()}
                onDoubleClick={() => setDisplayTooltip(true)}
                className="mst-activate-button"
                aria-label="Double-click to display tooltip"
            />
        );
    }

    // ── Cell tooltip: multi-season editor (after click / double-click) ─
    if (location === 'cell' && !isCalculated && displayTooltip) {
        return (
            <div ref={containerRef} className={openOnLeft ? 'open-left' : ''}>
                <div className="tooltip-container-mst flex-column">
                    <div className="separator-mst">
                        <Label labelSize="normal">{kpiName} for week {week}</Label>
                    </div>
                    <div className="input-container-mst flex-row align-center">
                        <Label forId="endingSeasonValue" labelSize="normal">
                            Season {endingSeasonLabel}
                        </Label>
                        <div className="mst-input-wrapper">
                            <TextInput
                                name="endingSeasonValue"
                                size="small"
                                value={endingSeasonInput}
                                disabled={isReadonlyGrid || isGroundRow || isPastWeek || !isOldSeasonEnabled || isEndingSeasonNonPrimary}
                                onChange={handleEndingSeasonChange}
                                onFocus={handleInputFocus}
                                onBlur={handleEndingSeasonBlur}
                            />
                        </div>
                    </div>
                    <div className="input-container-mst flex-row align-center">
                        <Label forId="latestSeasonValue" labelSize="normal">
                            Season {latestSeasonLabel}
                        </Label>
                        <div className="mst-input-wrapper">
                            <TextInput
                                name="latestSeasonValue"
                                size="small"
                                value={latestSeasonInput}
                                disabled={isReadonlyGrid || isGroundRow || isPastWeek || !isLatestSeasonEnabled || isLatestSeasonNonPrimary}
                                onChange={handleLatestSeasonChange}
                                onFocus={handleInputFocus}
                                onBlur={handleLatestSeasonBlur}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
});

MultiSeasonTooltip.displayName = 'MultiSeasonTooltip';
